import { VirtualizedGrid } from "@arcadeai/design-system/components/ui/atoms/virtualized-grid";
import { EmptyState } from "@arcadeai/design-system/components/ui/molecules/empty-state";
import { ToolCard } from "@arcadeai/design-system/components/ui/molecules/tool-card";
import { useColumns } from "@arcadeai/design-system/hooks/use-columns";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";

import { useToolkitPicker } from "../hooks/use-toolkit-picker";
import type { SelectionMode } from "../index";
import { EmptyStateCTAs } from "./empty-state-ctas";
import { UnoptimizedToggle } from "./unoptimized-toggle";

// Tool card height (~120px) + row gap (12px) + padding buffer (4px)
const TOOL_CARD_ROW_HEIGHT = 136;

type ToolsListProps = {
  selectionMode: SelectionMode;
  scrollContainer: HTMLElement | null;
};

export function ToolsList({ selectionMode, scrollContainer }: ToolsListProps) {
  const {
    state: {
      filteredTools,
      toolkitCollection,
      selectedTools,
      showUnoptimizedToolkits,
      hasStarterToolkits,
      hiddenToolCount,
    },
    toggleTool,
    toggleUnoptimizedToolkits,
  } = useToolkitPicker();

  const columns = useColumns();

  if (filteredTools.size === 0) {
    // If there are hidden tools that match the search, show the toggle instead of empty state
    if (hasStarterToolkits && hiddenToolCount > 0) {
      return (
        <UnoptimizedToggle
          hiddenCount={hiddenToolCount}
          isShowingUnoptimized={showUnoptimizedToolkits}
          itemType="tool"
          onToggle={toggleUnoptimizedToolkits}
        />
      );
    }

    return (
      <EmptyState
        data-testid="empty-tools"
        description="Try a different search term"
        title="No tools found"
      >
        <EmptyStateCTAs />
      </EmptyState>
    );
  }

  // Split tools into selected and unselected
  const toolsArray = Array.from(filteredTools);
  const selectedToolItems = toolsArray.filter((t) =>
    selectedTools.has(t.fully_qualified_name)
  );
  const unselectedToolItems = toolsArray.filter(
    (t) => !selectedTools.has(t.fully_qualified_name)
  );

  const renderToolCard = (tool: (typeof toolsArray)[number]) => {
    const toolkit = toolkitCollection?.toolkitMap?.get(tool.toolkit?.name);
    const Icon = getToolkitIcon(toolkit?.metadata?.id);

    return (
      <ToolCard
        data-testid={`tool-card-${tool.fully_qualified_name}`}
        description={tool.description}
        fully_qualified_name={tool.fully_qualified_name}
        hideCheckbox={selectionMode === "single"}
        Icon={Icon}
        isSelected={selectedTools.has(tool.fully_qualified_name)}
        key={tool.fully_qualified_name}
        name={tool.name}
        onSelect={() => toggleTool(tool)}
        toolkitName={tool.toolkit?.name ?? "Unknown MCP Server"}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="tools-grid">
      {selectedToolItems.length > 0 && (
        <div className="space-y-3" data-testid="selected-tools-section">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-neutral-700 text-sm dark:text-neutral-200">
              Selected
            </h3>
            <span className="rounded-full bg-emerald-300 px-1.5 py-0.5 text-emerald-900 text-xs dark:bg-emerald-600 dark:text-emerald-200">
              {selectedToolItems.length}
            </span>
          </div>
          <VirtualizedGrid
            columns={columns}
            estimateRowSize={TOOL_CARD_ROW_HEIGHT}
            getItemKey={(t) => t.fully_qualified_name}
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
            items={selectedToolItems}
            renderItem={renderToolCard}
            rowGapPx={12}
            scrollElement={scrollContainer}
          />
        </div>
      )}

      {unselectedToolItems.length > 0 && (
        <div className="space-y-3">
          {selectedToolItems.length > 0 && (
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-neutral-700 text-sm dark:text-neutral-200">
                All tools
              </h3>
              <span className="rounded-full bg-neutral-200 px-1.5 py-0.5 text-neutral-800 text-xs dark:bg-neutral-800 dark:text-neutral-200">
                {unselectedToolItems.length}
              </span>
            </div>
          )}
          <VirtualizedGrid
            columns={columns}
            estimateRowSize={TOOL_CARD_ROW_HEIGHT}
            getItemKey={(t) => t.fully_qualified_name}
            gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
            items={unselectedToolItems}
            renderItem={renderToolCard}
            rowGapPx={12}
            scrollElement={scrollContainer}
          />
        </div>
      )}

      {/* Hidden results message and toggle */}
      {!!hasStarterToolkits && (
        <UnoptimizedToggle
          hiddenCount={hiddenToolCount}
          isShowingUnoptimized={showUnoptimizedToolkits}
          itemType="tool"
          onToggle={toggleUnoptimizedToolkits}
        />
      )}
    </div>
  );
}
